
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/auth";
import { createReagentRequest } from "@/services/reagentRequests";
import CategorySelect from "@/components/reagents/CategorySelect";
import FormField from "@/components/reagents/FormField";
import { Button } from "@/components/ui/button";
import AmountField from "@/components/reagents/AmountField";
import DescriptionField from "@/components/reagents/DescriptionField";
import StorageConditionsField from "@/components/reagents/StorageConditionsField";
import { useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { fetchCategories } from "@/services/categoryService";

const ReagentRequestForm: React.FC = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<Array<{id: string, name: string}>>([]);
  
  // Form state
  const [name, setName] = useState("");
  const [catalogueNumber, setCatalogueNumber] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState<number | "">("");
  const [amountUnit, setAmountUnit] = useState("");
  const [numberOfPackages, setNumberOfPackages] = useState<number | "">("");
  const [storageConditions, setStorageConditions] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");

  // Fetch categories only once on component mount
  useEffect(() => {
    let isMounted = true;
    
    const loadCategories = async () => {
      try {
        const fetchedCategories = await fetchCategories();
        if (isMounted) {
          setCategories(fetchedCategories);
        }
      } catch (err) {
        console.error("Error loading categories:", err);
        if (isMounted) {
          setError("Failed to load categories. Please try again.");
        }
      }
    };
    
    loadCategories();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Set default city and country from profile only once when profile is loaded
  useEffect(() => {
    if (profile) {
      if (profile.city && !city) {
        setCity(profile.city);
      }
      if (profile.country && !country) {
        setCountry(profile.country);
      }
    }
  }, [profile, city, country]);

  // Validation
  const validate = useCallback(() => {
    if (!name.trim()) {
      setError("Reagent name is required");
      return false;
    }
    return true;
  }, [name]);

  // Form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError("You must be logged in to submit a request");
      return;
    }
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const requestData = {
        user_id: user.id,
        name: name.trim(),
        catalogue_number: catalogueNumber.trim() || null,
        category_id: categoryId || null,
        description: description.trim() || null,
        amount: amount || null,
        amount_unit: amountUnit.trim() || null,
        number_of_packages: numberOfPackages || null,
        storage_conditions: storageConditions.trim() || null,
        city: city.trim() || null,
        country: country.trim() || null
      };
      
      const result = await createReagentRequest(requestData);
      
      if (result) {
        // Redirect to the reagent requests page on success
        navigate("/reagent-requests");
      }
    } catch (err) {
      console.error("Error submitting reagent request:", err);
      setError("Failed to submit request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <FormField
        id="name"
        label="Reagent Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Enter the name of the reagent you need"
        required
      />
      
      <FormField
        id="catalogueNumber"
        label="Catalogue Number"
        value={catalogueNumber}
        onChange={(e) => setCatalogueNumber(e.target.value)}
        placeholder="Optional catalogue number"
      />
      
      <CategorySelect 
        value={categoryId}
        onChange={setCategoryId}
        categories={categories}
      />
      
      <DescriptionField
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      
      <AmountField
        amount={{
          value: amount.toString(),
          unit: amountUnit
        }}
        onChange={(data) => {
          setAmount(data.value ? Number(data.value) : "");
          setAmountUnit(data.unit);
        }}
      />
      
      <FormField
        id="numberOfPackages"
        label="Number of Packages/Containers"
        value={numberOfPackages.toString()}
        onChange={(e) => {
          const value = e.target.value.trim();
          setNumberOfPackages(value ? parseInt(value) : "");
        }}
        placeholder="How many packages do you need?"
        type="number"
      />
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          id="city"
          label="City"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Where do you need this reagent"
        />
        
        <FormField
          id="country"
          label="Country"
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          placeholder="Country where you need this reagent"
        />
      </div>
      
      <StorageConditionsField
        value={storageConditions}
        onChange={(e) => setStorageConditions(e.target.value)}
      />
      
      <div className="flex justify-end space-x-4 pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => navigate("/reagent-requests")}
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting}
        >
          {isSubmitting ? "Submitting..." : "Submit Request"}
        </Button>
      </div>
    </form>
  );
};

export default ReagentRequestForm;
